import {
  IChecklistItemPopulated,
  IChecklistItemRow,
  IStampSettings,
  onChecklistRowChange,
} from "@mctypes/checklist.types";
import { IMinUserDepricated, Paths, PathValue } from "@mctypes/global.types";
import { ICheckTypes, ISingleCheckType } from "@mcapps/shared/schemas/checklistSchema";
import { ComboboxItem, Grid, Stack, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconCalendarWeek } from "@tabler/icons-react";
import React, { useMemo } from "react";
import { McTitle } from "../../../../../components";
import McInputNew from "../../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../../components/McInput/McTextAreaNew";
import { McMantineUserSelect } from "../../../../../components/McMantineUserSelect/McMantineUserSelect";
import StampCheck from "../../StampCheck";

const ChecklistDetailsRowChecksSection: React.FC<
  React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> & {
    checkTypes: ICheckTypes;
    users: IMinUserDepricated[];
    isItem: boolean;
    isTemplate: boolean;
    editable: boolean;
    checklistRow: IChecklistItemRow;
    onValueChange: onChecklistRowChange;
    onAssignedToChange: <T extends keyof IChecklistItemRow>(
      path: string,
      newValue: IChecklistItemRow[T],
      oldValue: string,
    ) => void;
    updateRowAttribute: <P extends Paths<IChecklistItemPopulated, 4>>(
      rowId: string,
      path: P,
      oldValue: string,
      newValue: PathValue<IChecklistItemPopulated, P>,
    ) => void;
    stampSettings: IStampSettings;
  }
> = ({
  editable,
  onValueChange,
  onAssignedToChange,
  checklistRow,
  isItem,
  isTemplate,
  checkTypes,
  users,
  updateRowAttribute,
  stampSettings,
}) => {
  const checkColumns = useMemo(() => {
    const res: ((ISingleCheckType & { prop: keyof ICheckTypes }) | null)[] = [];

    Object.keys(checkTypes).forEach((e: keyof ICheckTypes) => {
      const checkType: ISingleCheckType & { prop: keyof ICheckTypes } = { ...checkTypes[e], prop: e };

      if (checkType.active === true) {
        res.push(checkType);
      }
    });

    while (res.length < 3) {
      res.push(null);
    }

    return res;
  }, [checkTypes]);

  const usersComboboxData: ComboboxItem[] = useMemo(
    () =>
      users
        .map((user) => ({
          value: user?._id,
          label: user?.name,
        }))
        .filter((user) => user.value != null && user.label != null),
    [users],
  );

  const getUserId = (user?: string | IMinUserDepricated | null) => {
    if (user == null) return null;
    if (typeof user === "string") return user;
    return user._id ?? null;
  };

  const getUserName = (user?: string | IMinUserDepricated | null) => {
    if (user == null) return null;
    if (typeof user === "string") return user;
    return user.name;
  };

  return (
    <Stack gap="sm">
      {isItem === true ? (
        <>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <McTitle title="Nummer">
                <Text size="sm">{checklistRow.itemId}</Text>
              </McTitle>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 8 }}>
              <McTitle title="Kategori">
                <Text size="sm">{`${checklistRow.categoryId}: ${checklistRow.originalCategory.title}`}</Text>
              </McTitle>
            </Grid.Col>
          </Grid>
          {isTemplate === false && (
            <Stack gap="xs">
              {checkColumns.map((checkType, i) => (
                <div key={i}>
                  {checkType != null && (
                    <McTitle title={checkType.name}>
                      <StampCheck
                        disabled={checklistRow.inactive.isChecked === true}
                        toggle={(rowId, oldValue, newValue) =>
                          updateRowAttribute(rowId, `checks.${checkType.prop}.status`, oldValue, newValue)
                        }
                        checkItem={checklistRow?.originalItem?.checks[checkType.prop]}
                        row={checklistRow}
                        stampSettings={stampSettings}
                      />
                    </McTitle>
                  )}
                </div>
              ))}
            </Stack>
          )}
        </>
      ) : (
        <>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <McTitle title="Kategorinummer">
                {editable === true ? (
                  <McInputNew
                    required={true}
                    value={checklistRow.categoryId}
                    pattern="^\d+$"
                    onBlur={(e) => onValueChange(checklistRow.categoryId, Number(e.target.value), "categoryId")}
                  />
                ) : (
                  <Text size="sm">{checklistRow.categoryId}</Text>
                )}
              </McTitle>
            </Grid.Col>

            {isTemplate === false && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <McTitle title="Frist">
                  <DateInput
                    styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
                    clearable
                    valueFormat="DD.MM.YYYY"
                    value={
                      checklistRow.originalCategory.dueDate != null
                        ? new Date(checklistRow.originalCategory.dueDate)
                        : null
                    }
                    onChange={(value) =>
                      onValueChange(
                        checklistRow.dueDate,
                        value != null ? (value as unknown as Date).toISOString() : undefined,
                        "dueDate",
                      )
                    }
                    rightSection={
                      checklistRow.originalCategory.dueDate == null && (
                        <IconCalendarWeek color="var(--mantine-color-warmgray-3)" />
                      )
                    }
                  />
                </McTitle>
              </Grid.Col>
            )}
          </Grid>

          {isTemplate === false && (
            <Grid>
              {checkColumns.map((checkType, i) => (
                <Grid.Col span={{ base: 12, md: 4 }} key={i}>
                  {checkType != null && (
                    <McTitle title={`Ansvarlig ${checkType.name}`}>
                      <McMantineUserSelect
                        data={usersComboboxData}
                        value={getUserId(checklistRow?.originalCategory?.checks?.[checkType.prop]?.assignedTo)}
                        onChange={(value) =>
                          onAssignedToChange(
                            `checks.${checkType.prop}.assignedTo`,
                            value,
                            getUserName(checklistRow?.originalCategory?.checks?.[checkType.prop]?.assignedTo) ?? "",
                          )
                        }
                      />
                    </McTitle>
                  )}
                </Grid.Col>
              ))}
            </Grid>
          )}
        </>
      )}

      {isTemplate === false && isItem === true && (
        <McTitle title="Rapportkommentar">
          <McTextAreaNew
            onBlur={(e) => onValueChange(checklistRow.comment, e.target.value, "comment")}
            value={checklistRow.comment}
            autosize
          />
        </McTitle>
      )}
    </Stack>
  );
};

export default ChecklistDetailsRowChecksSection;
